import React, { useState } from "react"
import styled from "styled-components"
import Button from "../reusable/button"
import { StaticImage } from "gatsby-plugin-image"
import theme from "@src/styles/theme"
import PropTypes from "prop-types"
import { why10ATestimonial } from "@src/utils/constants/video-testimonials"
import YoutubeModal from "@src/components/youtube-player-modal"
import { Link } from "gatsby"
import Image from "../core-image"

const SectionWrapper = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 40px;

  @media (max-width: 1000px) {
    flex-direction: column;
    gap: 32px;
  }
`

const TextWrapper = styled.div`
  width: 60%;
  max-width: 698px;

  @media (max-width: 1260px) {
    width: 50%;
  }
  @media (max-width: 1000px) {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    margin-right: auto;
    max-width: unset;
  }
`

const Subtitle = styled.p`
  color: ${theme.blackShade};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
`
const Description = styled.p`
  color: ${theme.silverShade};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  p {
    color: ${theme.silverShade};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.5px;
  }
`

const VideoCta = styled.div`
  color: ${theme.whiteShade};
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  width: 80%;

  position: absolute;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`

const VideoWrapper = styled.div`
  width: 100%;
  position: relative;

  border-radius: 8px;
  overflow: hidden;
  max-height: 209px;
  max-width: 384px;

  .gatsby-image-wrapper {
    transform: translate(0, -20px);
  }

  @media (max-width: 1000px) {
    width: 100%;
    height: 49vw;
    max-width: 550px;
  }

  @media (max-width: 550px) {
    max-width: 100%;
  }
`

const VideoOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.35) 0%,
    rgba(0, 0, 0, 0.35) 100%
  );
  z-index: 1;
`

const PlayButton = styled.div`
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #1f1f23;
  font-size: 2rem;
  z-index: 2;

  @media (max-width: 420px) {
    font-size: 1.4rem;
    top: 30%;

    width: 40px;
    height: 40px;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 24px;
  text-align: center;
`

const ButtonHolder = styled.div`
  display: flex;
  max-width: 450px;
  gap: 14px;

  @media (max-width: 1000px) {
    width: 100%;
    max-width: 550px;
    justify-content: center;
  }
  @media (max-width: 550px) {
    width: 100%;
    flex-direction: column;
    max-width: 100%;
  }
`

const ReviewLink = styled(Link)`
  text-decoration: none;
`
const ActivityTitle = styled.p`
  color: ${theme.blackShade};
  font-weight: 600;
  font-size: 18px;
  line-height: 27px;
  letter-spacing: 0.25px;
  margin-bottom: 0.5em;
`
const TopSeoRegionsWrapper = styled.div`
  margin-bottom: 1em;
`
const TopSeoRegionsLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.25px;

  a {
    font-family: system-ui;
    background: none;
    border: none;
    border-radius: 4px;
    padding: 6px 12px;
    background-color: rgb(228, 245, 238);
    color: #315254;
    cursor: pointer;
    text-decoration: none;
    &:hover {
      background-color: #daece4;
      border-radius: 4px;
      color: #3a6968;
    }
  }

  button.link-style {
    padding: 4px 8px;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    font-family: inherit;
  }
`

const HeroTour = ({
  pageName,
  headerText,
  hiking,
  bike,
  subtitle,
  topSeoRegions,
}) => {
  const replaceHeaderText = headerText?.replace("<p>", "")?.replace("</p>", "")
  const [showVideo, setShowVideo] = useState(false)
  const [showAllRegions, setShowAllRegions] = useState(false)
  const maxVisible = 6
  const visibleRegions = topSeoRegions?.length
    ? showAllRegions
      ? topSeoRegions
      : topSeoRegions.slice(0, maxVisible)
    : []
  const handleVideoClose = () => setShowVideo(false)

  return (
    <SectionWrapper>
      <TextWrapper>
        <Subtitle>
          {subtitle
            ? subtitle
            : pageName
              ? `Booking one of these amazing ${pageName} will open up an
          entirely new world full of adventure for you.`
              : "Browse our selection of tours below and open yourself up to the exciting world of adventure travel."}
        </Subtitle>
        <Description>{replaceHeaderText}</Description>
        {topSeoRegions?.length > 0 && (
          <>
            <ActivityTitle>Explore by destination</ActivityTitle>

            <TopSeoRegionsWrapper>
              <TopSeoRegionsLinks>
                {visibleRegions.map(region => (
                  <a key={region.slug} href={region.uri}>
                    {region.name}
                  </a>
                ))}
                {topSeoRegions.length > maxVisible && (
                  <button
                    className="link-style"
                    onClick={() => setShowAllRegions(prev => !prev)}
                  >
                    {showAllRegions ? "See less" : "See all"}
                  </button>
                )}
              </TopSeoRegionsLinks>
            </TopSeoRegionsWrapper>
          </>
        )}
        {(hiking || bike) && (
          <>
            <ActivityTitle>Explore by activity</ActivityTitle>
            <ButtonHolder>
              {hiking && (
                <Button type="greenShade" to={hiking.url}>
                  <Image
                    src="/images/tour-page/HikingIcon.svg"
                    alt="hiking-image"
                  />
                  <p>{hiking.name} Hiking Tours</p>
                </Button>
              )}
              {bike && (
                <Button type="greenShade" to={bike.url}>
                  <Image
                    src="/images/tour-page/CyclingIcon.svg"
                    alt="biking-image"
                  />
                  <p>{bike.name} Cycling Tours</p>
                </Button>
              )}
            </ButtonHolder>
          </>
        )}
      </TextWrapper>
      <div>
        <VideoWrapper onClick={() => setShowVideo(true)}>
          <VideoOverlay />
          <StaticImage
            src="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-page/TourThumbnail.jpeg"
            alt="Africa Adventure"
          />
          <PlayButton>▶</PlayButton>
          <VideoCta>
            Hear why personalized and private travel is just <i>better</i>.
          </VideoCta>
        </VideoWrapper>
        {pageName && (
          <ButtonWrapper>
            <ActivityTitle>Travellers love 10Adventures</ActivityTitle>
            <ReviewLink to="/testimonials/">
              <Button type="review" text="Read our reviews" />
            </ReviewLink>
          </ButtonWrapper>
        )}
      </div>
      {showVideo ? (
        <YoutubeModal
          id="why10a-video"
          isOpen={showVideo}
          onClose={handleVideoClose}
          videoId={why10ATestimonial.url.slice(-11)}
        />
      ) : null}
    </SectionWrapper>
  )
}

HeroTour.propTypes = {
  pageName: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  scrollToFilter: PropTypes.func,
}

export default HeroTour
